import { Remirror, useRemirror, ThemeProvider } from "@remirror/react";
import { PlaceholderExtension } from "remirror/extensions";
import {
	Toolbar,
	HistoryButtonGroup,
	VerticalDivider,
	DataTransferButtonGroup,
	HeadingLevelButtonGroup,
	BasicFormattingButtonGroup,
	ListButtonGroup,
} from "@remirror/react-ui";
import { prosemirrorNodeToHtml } from "remirror";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { wysiwygPreset } from "remirror/extensions";
import "remirror/styles/all.css";
import "./BoQLongTextEditor.scss";

/**
 * WYSIWYG long-text editor for BoQ position rows (Remirror).
 * Persists HTML to parent on every state change via prosemirrorNodeToHtml.
 */
const BoQLongTextEditor = ({
	value = "",
	onChange,
	className,
	placeholder,
}) => {
	const { t } = useTranslation();
	const initialContent = value && value.trim() ? value : "<p></p>";

	const { manager, state, setState } = useRemirror({
		extensions: () => [
			...wysiwygPreset(),
			...(placeholder ? [new PlaceholderExtension({ placeholder })] : []),
		],
		content: initialContent,
		stringHandler: "html",
	});

	const i18nFormat = useCallback(
		(message, values) => {
			if (!message?.id) return String(message);
			return t(message.id, values ?? {});
		},
		[t],
	);

	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	// Debounce timer: flush HTML to parent at most once per 300 ms.
	// This prevents every keystroke from updating `rows` state (and re-arming
	// the page-level auto-save timer) while the user is still typing.
	const debounceRef = useRef(null);
	useEffect(() => () => clearTimeout(debounceRef.current), []);

	const handleRemirrorChange = useCallback((parameter) => {
		setState(parameter.state);

		if (parameter.tr?.docChanged) {
			const html = prosemirrorNodeToHtml(parameter.state.doc);
			clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(() => {
				onChangeRef.current?.(html);
			}, 300);
		}
	}, [setState]);

	return (
		<div
			className={`remirror-theme boq-long-text-editor ${className ?? ""}`.trim()}
			onClick={(e) => e.stopPropagation()}
		>
			<ThemeProvider>
				<Remirror
					manager={manager}
					state={state}
					onChange={handleRemirrorChange}
					autoRender="end"
					i18nFormat={i18nFormat}
					classNames={["boq-long-text-editor-inner"]}
				>
					<Toolbar>
						<HistoryButtonGroup />
						<VerticalDivider />
						<DataTransferButtonGroup />
						<VerticalDivider />
						<HeadingLevelButtonGroup />
						<VerticalDivider />
						<BasicFormattingButtonGroup />
						<VerticalDivider />
						<ListButtonGroup />
					</Toolbar>
				</Remirror>
			</ThemeProvider>
		</div>
	);
};

export default BoQLongTextEditor;
